import { forwardRef, useState } from 'react';
import styled from '@emotion/styled';
import { Icon, PasswordInput, tokens } from '@hotjar/hotsauce';
import type { PasswordInputProps } from '@hotjar/hotsauce';
import { ErrorText } from '@hotjar/webapp/components/ErrorText';

import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { allValid, validate, validators } from './utils';

const PASSWORD_FIELD_ID = 'password';
const PASSWORD_FIELD_NAME = 'password';

const validatedStyles = css`
  color: ${tokens.color.text_success};
`;

const Wrapper = styled.div`
  position: relative;
`;
const Label = styled.label`
  display: inline-block;
  margin-bottom: ${tokens.spacer.x1};

  text-transform: initial;
  ${tokens.text('subtitle2')};
`;

const RequiredValidators = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  min-height: 60px;
  margin-bottom: ${tokens.spacer.x2};
`;

const ValidatorItem = styled.li`
  display: inline-block;
  width: 100%;
  ${tokens.transition()};

  ${tokens.text('caption')};
  color: ${tokens.color.text_disabled};

  margin-bottom: ${tokens.spacer.x1};
  margin-right: ${tokens.spacer.x2};
  padding-top: ${tokens.spacer.x1};
  &:nth-of-type(3) {
    margin-right: 0;
  }

  & svg {
    margin-right: ${tokens.spacer.x0};
    margin-bottom: 2px;
  }

  @media (min-width: 768px) {
    width: auto;
  }
`;

interface PasswordInputWithValidationProps extends Omit<PasswordInputProps, 'onChange'> {
  id?: string;
  name?: string;
  label?: string;
  isPasswordValidCallback: (val: boolean | '') => void;
  onChange?: (value: string) => void;
  // FIXME
  // eslint-disable-next-line react/no-unused-prop-types
  companyName?: string;
  placeholder?: string;
  defaultValue?: string;
  // FIXME
  // eslint-disable-next-line react/no-unused-prop-types
  large?: boolean;
  required: boolean;
}

export const PasswordInputWithValidation = forwardRef<
  HTMLInputElement,
  PasswordInputWithValidationProps
>(
  (
    {
      isPasswordValidCallback,
      onChange,
      id = PASSWORD_FIELD_ID,
      name = PASSWORD_FIELD_NAME,
      label = 'common:login.form.password',
      placeholder = 'common:login.form.password-placeholder',
      defaultValue = '',
      required = false,
      ...props
    }: PasswordInputWithValidationProps,
    ref,
  ): JSX.Element => {
    const { t } = useTranslation();
    const [passwordValid, setPasswordValid] = useState(true);
    const [value, setValue] = useState(defaultValue);

    const requiredValidators = validators.slice(0, 3);
    const maxLenghtValidator = validators[3];

    const updateValidationState = (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      setValue(inputValue);

      const validationValue = allValid(
        validate([...requiredValidators, maxLenghtValidator], inputValue),
      );
      setPasswordValid(validationValue);
      isPasswordValidCallback(validationValue);

      if (onChange) {
        onChange(inputValue);
      }
    };

    const isLengthValid = maxLenghtValidator.validatorFn(value);
    const showErrorMessage = required && value && !passwordValid;

    return (
      <Wrapper>
        <Label htmlFor={name}>{t(label)}</Label>
        <PasswordInput
          id={id}
          name={name}
          size="l"
          ref={ref}
          data-testid="password-input"
          onChange={updateValidationState}
          isInvalid={required && (!value || !passwordValid)}
          placeholder={t(placeholder)}
          {...props}
        />

        <div>
          {required && !value && (
            <ErrorText data-testid="password-field-error">
              {t('common:registration.validation-errors.required')}
            </ErrorText>
          )}
          {showErrorMessage && (
            <ErrorText>
              {isLengthValid
                ? t('common:registration.validation-errors.strong-password')
                : t('common:registration.validation-errors.valid-password')}
            </ErrorText>
          )}
        </div>

        <RequiredValidators>
          {requiredValidators.map((validator) => {
            const isValidated = validator.validatorFn(value);
            return (
              <ValidatorItem key={validator.caption} css={[isValidated && validatedStyles]}>
                {isValidated ? (
                  <Icon size="m" icon="check_alt" color="text_success" />
                ) : (
                  <Icon size="m" icon="check" />
                )}
                {validator.caption && t(validator.caption)}
              </ValidatorItem>
            );
          })}

          {!isLengthValid && (
            <ValidatorItem>
              <Icon size="m" icon="check" />
              {maxLenghtValidator.caption && t(maxLenghtValidator.caption)}
            </ValidatorItem>
          )}
        </RequiredValidators>
      </Wrapper>
    );
  },
);
